import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import { parseFlowVolumeTooltipRange } from 'utils/flowVolumeChart';

export const CHART_TOOLTIP_CURSOR_BAR = {
  fill: 'rgba(20, 152, 203, 0.06)',
  stroke: '#cbd5e1',
  strokeWidth: 1,
};

export const CHART_TOOLTIP_CURSOR_LINE = {
  stroke: 'rgba(20, 152, 203, 0.25)',
  strokeWidth: 1,
};

export const CHART_TOOLTIP_WRAPPER_STYLE = {
  zIndex: 20,
  outline: 'none',
};

export const FLOW_COLOR = Colors.lightBlue;
export const PERMEATE_COLOR = '#97D977';
export const PERMEATE_VALUE_COLOR = '#6CB84E';
export const PRESSURE_COLORS = ['#2563eb', '#22c55e', '#f59e0b'];
export const PRESSURE_LABELS = ['Filter 1 Max PD', 'Filter 2 Max PD', 'Filter 3 Max PD'];

const TooltipCard = styled.div`
  background: #fff;
  border: 1px solid #e2ebf3;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 10px 28px rgba(49, 70, 131, 0.14);
  min-width: 210px;
`;

const TooltipHeader = styled.div`
  padding-bottom: 12px;
  margin-bottom: 10px;
  border-bottom: 1px solid #eef4f8;
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.blue};
  line-height: 1.35;
`;

const TooltipSubtitle = styled.div`
  margin-top: 3px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.35;
`;

const MetricList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const MetricLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #475569;
`;

const MetricSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${props => props.$color};
  flex-shrink: 0;
`;

const MetricValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$color};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const TooltipSection = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef4f8;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const TooltipFooter = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef4f8;
  font-size: 11px;
  color: #64748b;
  text-align: right;

  strong {
    color: ${Colors.blue};
    font-weight: 600;
  }
`;

const ChartTooltipShell = ({ title, subtitle, children, footer }) => (
  <TooltipCard>
    {(title || subtitle) && (
      <TooltipHeader>
        {title && <TooltipTitle>{title}</TooltipTitle>}
        {subtitle && <TooltipSubtitle>{subtitle}</TooltipSubtitle>}
      </TooltipHeader>
    )}
    {children}
    {footer}
  </TooltipCard>
);

ChartTooltipShell.propTypes = {
  children: PropTypes.node,
  footer: PropTypes.node,
  subtitle: PropTypes.string,
  title: PropTypes.string,
};

export const ChartTooltipMetric = ({ color, label, value }) => (
  <MetricRow>
    <MetricLabel>
      <MetricSwatch $color={color} />
      {label}
    </MetricLabel>
    <MetricValue $color={color}>{value}</MetricValue>
  </MetricRow>
);

ChartTooltipMetric.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export const ChartTooltipMetrics = ({ children }) => <MetricList>{children}</MetricList>;

ChartTooltipMetrics.propTypes = {
  children: PropTypes.node,
};

export const ChartTooltipSection = ({ title, children }) => (
  <TooltipSection>
    {title && <SectionLabel>{title}</SectionLabel>}
    {children}
  </TooltipSection>
);

ChartTooltipSection.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
};

export const buildPressureMetrics = (point, labels = PRESSURE_LABELS, colors = PRESSURE_COLORS) =>
  labels
    .map((label, index) => ({
      key: `psi_${index}`,
      label,
      color: colors[index],
      value: point[`psi_${index}`],
    }))
    .filter(metric => metric.value !== undefined && metric.value !== null);

const getMetricLineLabel = valueLabel => {
  if (valueLabel === 'PPM') {
    return 'Average TDS';
  }
  if (valueLabel === '%') {
    return 'Waste water';
  }
  return 'Value';
};

const getPayloadPoint = (active, payload) => {
  if (!active || !payload || !payload[0]) {
    return null;
  }
  return payload[0].payload;
};

export const FlowVolumeTooltip = ({ active, payload }) => {
  const point = getPayloadPoint(active, payload);
  if (!point) {
    return null;
  }

  const { title, subtitle } = parseFlowVolumeTooltipRange(point);
  const pressureMetrics = buildPressureMetrics(point);

  return (
    <ChartTooltipShell title={title} subtitle={subtitle}>
      <ChartTooltipMetrics>
        <ChartTooltipMetric
          color={FLOW_COLOR}
          label="Flow"
          value={`${Math.round(point.gal || 0).toLocaleString()} gal`}
        />
      </ChartTooltipMetrics>
      {pressureMetrics.length > 0 && (
        <ChartTooltipSection title="Pressure drop">
          {pressureMetrics.map(metric => (
            <ChartTooltipMetric
              key={metric.key}
              color={metric.color}
              label={metric.label}
              value={`${Math.round(metric.value)} PSI`}
            />
          ))}
        </ChartTooltipSection>
      )}
    </ChartTooltipShell>
  );
};

FlowVolumeTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

export const RoFlowVolumeTooltip = ({ active, payload }) => {
  const point = getPayloadPoint(active, payload);
  if (!point) {
    return null;
  }

  const inletGal = Math.round(point.galInlet || 0);
  const permeateGal = Math.round(point.gal || 0);
  const { title, subtitle } = parseFlowVolumeTooltipRange(point);
  const recoveryPct = inletGal > 0 ? Math.round((permeateGal / inletGal) * 100) : null;

  return (
    <ChartTooltipShell
      title={title}
      subtitle={subtitle}
      footer={
        recoveryPct != null ? (
          <TooltipFooter>
            Recovery: <strong>{recoveryPct}%</strong>
          </TooltipFooter>
        ) : null
      }
    >
      <ChartTooltipMetrics>
        <ChartTooltipMetric
          color={FLOW_COLOR}
          label="Inlet Flow"
          value={`${inletGal.toLocaleString()} gal`}
        />
        <ChartTooltipMetric
          color={PERMEATE_VALUE_COLOR}
          label="Permeate Flow"
          value={`${permeateGal.toLocaleString()} gal`}
        />
      </ChartTooltipMetrics>
    </ChartTooltipShell>
  );
};

RoFlowVolumeTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

export const MetricLineTooltip = ({ active, payload, valueLabel }) => {
  const point = getPayloadPoint(active, payload);
  if (!point) {
    return null;
  }

  const { title, subtitle } = parseFlowVolumeTooltipRange(point);
  const metricLabel = getMetricLineLabel(valueLabel);
  const value = Math.round(point.gal || 0);

  return (
    <ChartTooltipShell title={title} subtitle={subtitle}>
      <ChartTooltipMetrics>
        <ChartTooltipMetric
          color={FLOW_COLOR}
          label={metricLabel}
          value={`${value.toLocaleString()} ${valueLabel}`}
        />
      </ChartTooltipMetrics>
    </ChartTooltipShell>
  );
};

MetricLineTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  valueLabel: PropTypes.string,
};

export const MaxFlowPressureTooltip = ({ active, payload }) => {
  const point = getPayloadPoint(active, payload);
  if (!point) {
    return null;
  }

  const { title, subtitle } = parseFlowVolumeTooltipRange(point);
  const pressureMetrics = buildPressureMetrics(point, [
    'Filter 1 max PD',
    'Filter 2 max PD',
    'Filter 3 max PD',
  ]);

  return (
    <ChartTooltipShell title={title} subtitle={subtitle}>
      {point.gal != null && (
        <ChartTooltipMetrics>
          <ChartTooltipMetric
            color={FLOW_COLOR}
            label="Max flow rate"
            value={`${Number(point.gal).toFixed(2)} GPM`}
          />
        </ChartTooltipMetrics>
      )}
      {pressureMetrics.length > 0 && (
        <ChartTooltipSection title="Pressure drop">
          {pressureMetrics.map(metric => (
            <ChartTooltipMetric
              key={metric.key}
              color={metric.color}
              label={metric.label}
              value={`${Number(metric.value).toFixed(2)} PSI`}
            />
          ))}
        </ChartTooltipSection>
      )}
    </ChartTooltipShell>
  );
};

MaxFlowPressureTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};
